import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePlayerCache } from '../contexts/PlayerCacheContext';
import { useMatchCache } from '../contexts/MatchCacheContext';
import { collection, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { formatDateTime, formatTime, sortMatchesChronologically } from '../utils/dateFormat';
import { formatScheduledDateTimeInTimezone, DEFAULT_TIMEZONE } from '../utils/dateTimeUtils';
import PlayerNameWithApartment from '../components/PlayerNameWithApartment';
import TeamStatsLink from '../components/TeamStatsLink';
import './MatchHistory.css';

export default function MatchHistory() {
  const { tournamentId } = useParams();
  const { currentUser, userProfile } = useAuth();
  const userTimezone = userProfile?.timezone || DEFAULT_TIMEZONE;
  const navigate = useNavigate();
  const { getPlayers, getPlayerName } = usePlayerCache();
  const { fetchDeltaMatches } = useMatchCache();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'singles', 'doubles', 'completed', 'scheduled'

  useEffect(() => {
    if (currentUser) {
      fetchMatches();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, tournamentId, filter]);

  async function fetchMatches() {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Step 1: Get all tournament IDs the player is registered with
      const registrationsQuery = query(
        collection(db, 'registrations'),
        where('playerId', '==', currentUser.uid)
      );
      const registrationsSnapshot = await getDocs(registrationsQuery);
      
      const tournamentIds = new Set();
      if (tournamentId) {
        tournamentIds.add(tournamentId); // Include current tournament
      }
      
      registrationsSnapshot.forEach(doc => {
        const data = doc.data();
        if (data.tournamentId) {
          tournamentIds.add(data.tournamentId);
        }
      });

      if (tournamentIds.size === 0) {
        setMatches([]);
        setLoading(false);
        return;
      }

      // Step 2: Fetch tournament names for display
      const tournamentNamesMap = {};
      const tournamentPromises = Array.from(tournamentIds).map(async (tid) => {
        try {
          const tournamentDoc = await getDoc(doc(db, 'tournaments', tid));
          if (tournamentDoc.exists()) {
            tournamentNamesMap[tid] = tournamentDoc.data().name || 'Unknown Tournament';
          } else {
            tournamentNamesMap[tid] = 'Unknown Tournament';
          }
        } catch (error) {
          console.error(`Error fetching tournament ${tid}:`, error);
          tournamentNamesMap[tid] = 'Unknown Tournament';
        }
      });
      await Promise.all(tournamentPromises);

      // Step 3: Fetch matches from all tournaments
      const allMatches = [];
      for (const tid of tournamentIds) {
        // Determine status filter
        let statusFilter = null;
        if (filter === 'completed') {
          statusFilter = 'completed';
        } else if (filter === 'scheduled') {
          statusFilter = 'scheduled';
        }

        // Fetch matches for this tournament
        const result = await fetchDeltaMatches(tid, statusFilter);
        
        // Filter by tournament ID and match type if needed
        let tournamentMatches = result.all.filter(m => m.tournamentId === tid);
        if (filter === 'singles') {
          tournamentMatches = tournamentMatches.filter(m => m.matchType === 'singles');
        } else if (filter === 'doubles') {
          tournamentMatches = tournamentMatches.filter(m => m.matchType === 'doubles');
        }
        
        allMatches.push(...tournamentMatches);
      }

      // Step 4: Sort matches by tournament, then round, then chronological (date + time)
      allMatches.sort((a, b) => {
        const aTournamentName = tournamentNamesMap[a.tournamentId] || '';
        const bTournamentName = tournamentNamesMap[b.tournamentId] || '';
        if (aTournamentName !== bTournamentName) return aTournamentName.localeCompare(bTournamentName);
        if (a.round !== b.round) return a.round - b.round;
        return sortMatchesChronologically(a, b);
      });

      // Step 5: Assign match numbers per tournament
      const matchNumbersByTournament = {};
      allMatches.forEach(match => {
        if (!matchNumbersByTournament[match.tournamentId]) {
          matchNumbersByTournament[match.tournamentId] = 0;
        }
        matchNumbersByTournament[match.tournamentId]++;
      });

      // Reset counters and assign numbers
      const currentCounters = {};
      Object.keys(matchNumbersByTournament).forEach(tid => {
        currentCounters[tid] = 0;
      });

      // Collect all player IDs we need
      const playerIds = new Set();
      allMatches.forEach(match => {
        if (match.matchType === 'doubles') {
          if (match.team1Players) match.team1Players.forEach(id => playerIds.add(id));
          if (match.team2Players) match.team2Players.forEach(id => playerIds.add(id));
        } else {
          if (match.player1Id) playerIds.add(match.player1Id);
          if (match.player2Id) playerIds.add(match.player2Id);
        }
        if (match.refereeId) playerIds.add(match.refereeId);
      });

      // Batch fetch all players at once
      await getPlayers(Array.from(playerIds));

      // Step 6: Enrich matches with player names, tournament names, and match numbers
      const matchesWithNames = allMatches.map((match) => {
        const matchNumber = ++currentCounters[match.tournamentId]; // Sequential match number per tournament
        
        const enrichedMatch = {
          ...match,
          matchNumber,
          tournamentName: tournamentNamesMap[match.tournamentId] || 'Unknown Tournament'
        };
        
        if (match.matchType === 'doubles') {
          const team1Names = match.team1Players 
            ? match.team1Players.map(id => getPlayerName(id)).filter(name => name !== 'Unknown')
            : [];
          const team2Names = match.team2Players 
            ? match.team2Players.map(id => getPlayerName(id)).filter(name => name !== 'Unknown')
            : [];
          return { ...enrichedMatch, team1Names, team2Names };
        } else {
          const player1Name = match.player1Id ? getPlayerName(match.player1Id) : (match.player1Name || 'Player 1');
          const player2Name = match.player2Id ? getPlayerName(match.player2Id) : (match.player2Name || 'Player 2');
          return { ...enrichedMatch, player1Name, player2Name };
        }
      });

      setMatches(matchesWithNames);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching matches:', error);
      setLoading(false);
    }
  }

  // Legacy function removed - using real-time listener instead

  function formatScoreSummary(scores) {
    if (!scores) return 'No scores';
    
    const games = [];
    if (scores.game1) games.push(`${scores.game1.player1}-${scores.game1.player2}`);
    if (scores.game2) games.push(`${scores.game2.player1}-${scores.game2.player2}`);
    if (scores.game3) games.push(`${scores.game3.player1}-${scores.game3.player2}`);
    
    return games.join(', ');
  }

  // Function removed - not currently used
  // function getServiceHistory(serviceState) {
  //   if (!serviceState || !serviceState.services) return [];
  //   return serviceState.services.map((service, index) => ({
  //     ...service,
  //     serviceNumber: index + 1
  //   }));
  // }


  if (loading) {
    return <div className="loading">Loading matches...</div>;
  }

  return (
    <div className="match-history-container">
      <header className="match-history-header">
        <div>
          <h1 className="page-title">Match History</h1>
          <p className="page-subtitle">Past and upcoming matches</p>
        </div>
        {tournamentId ? (
          <button type="button" onClick={() => navigate(`/tournament/${tournamentId}/dashboard`)} className="btn-secondary">
            Back to Dashboard
          </button>
        ) : (
          <button type="button" onClick={() => navigate('/dashboard')} className="btn-secondary">
            Back to Dashboard
          </button>
        )}
      </header>

      <div className="match-filters">
        <button
          type="button"
          className={filter === 'all' ? 'match-filter-btn filter-active' : 'match-filter-btn'}
          onClick={() => setFilter('all')}
        >
          All Matches
        </button>
        <button
          type="button"
          className={filter === 'singles' ? 'match-filter-btn filter-active' : 'match-filter-btn'}
          onClick={() => setFilter('singles')}
        >
          Singles
        </button>
        <button
          type="button"
          className={filter === 'doubles' ? 'match-filter-btn filter-active' : 'match-filter-btn'}
          onClick={() => setFilter('doubles')}
        >
          Doubles
        </button>
        <button
          type="button"
          className={filter === 'completed' ? 'match-filter-btn filter-active' : 'match-filter-btn'}
          onClick={() => setFilter('completed')}
        >
          Completed
        </button>
        <button
          type="button"
          className={filter === 'scheduled' ? 'match-filter-btn filter-active' : 'match-filter-btn'}
          onClick={() => setFilter('scheduled')}
        >
          Scheduled
        </button>
      </div>

      <div className="matches-list">
        {matches.length === 0 ? (
          <div className="no-matches">
            <p>No matches found.</p>
          </div>
        ) : (
          matches.map(match => (
            <div key={match.id} className="match-history-card card-ui">
              <div className="match-header">
                <div className="match-info">
                  <span className="match-number-badge">Match #{match.matchNumber || 'N/A'}</span>
                  <span className="round-badge">Round {match.round || 'N/A'}</span>
                  <span className={`match-type-badge ${match.matchType}`}>
                    {match.matchType === 'doubles' ? '👥 Doubles' : '👤 Singles'}
                  </span>
                  <span className={`status-badge status-${match.status}`}>
                    {match.status || 'scheduled'}
                  </span>
                  {match.tournamentName && (
                    <span className="tournament-badge">{match.tournamentName}</span>
                  )}
                </div>
                <div className="match-meta">
                  {match.scheduledDate && (
                    <div className="match-datetime">
                      <span className="datetime-icon">📅</span>
                      <span className="datetime-text">
                        {formatScheduledDateTimeInTimezone(match.scheduledAt ?? match.scheduledDate, match.scheduledTime, userTimezone, { useSeparator: ' at ' })}
                      </span>
                    </div>
                  )}
                  {match.startedAt && !match.scheduledDate && (
                    <div className="match-datetime">
                      <span className="datetime-icon">▶️</span>
                      <span className="datetime-text">Started: {formatDateTime(match.startedAt)}</span>
                    </div>
                  )}
                  {match.completedAt && (
                    <div className="match-datetime">
                      <span className="datetime-icon">✅</span>
                      <span className="datetime-text">Completed: {formatDateTime(match.completedAt)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="match-players">
                {match.matchType === 'doubles' ? (
                  <>
                    <div className="team">
                      <div className="player-name">
                        {(match.team1Players || []).length >= 2 ? (
                          <TeamStatsLink playerIds={match.team1Players} className="team-stats-link">
                            {match.team1Name || match.team1Names?.join(' & ') || 'Team 1'}
                          </TeamStatsLink>
                        ) : (
                          (match.team1Name || match.team1Names?.join(' & ') || 'Team 1')
                        )}
                      </div>
                      {match.scores && (
                        <div className="score">
                          {formatScoreSummary(match.scores)}
                        </div>
                      )}
                    </div>
                    <div className="vs">VS</div>
                    <div className="team">
                      <div className="player-name">
                        {(match.team2Players || []).length >= 2 ? (
                          <TeamStatsLink playerIds={match.team2Players} className="team-stats-link">
                            {match.team2Name || match.team2Names?.join(' & ') || 'Team 2'}
                          </TeamStatsLink>
                        ) : (
                          (match.team2Name || match.team2Names?.join(' & ') || 'Team 2')
                        )}
                      </div>
                      {match.scores && (
                        <div className="score">
                          {formatScoreSummary(match.scores)}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="player">
                      <div className="player-name"><PlayerNameWithApartment playerId={match.player1Id} /></div>
                      {match.scores && (
                        <div className="score">
                          {formatScoreSummary(match.scores)}
                        </div>
                      )}
                    </div>
                    <div className="vs">VS</div>
                    <div className="player">
                      <div className="player-name"><PlayerNameWithApartment playerId={match.player2Id} /></div>
                      {match.scores && (
                        <div className="score">
                          {formatScoreSummary(match.scores)}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              <div className="match-footer">
                {match.refereeId && (
                  <div className="referee-info">
                    <span className="referee-label">Referee:</span>
                    <span className="referee-name"><PlayerNameWithApartment playerId={match.refereeId} /></span>
                  </div>
                )}
                <div className="match-actions">
                  <button
                    onClick={() => setSelectedMatch(match)}
                    className="btn-primary btn-small"
                  >
                    Details
                  </button>
                  {match.status !== 'completed' && (
                    <button
                      onClick={() => navigate(`/match/${match.id}`)}
                      className="btn-secondary btn-small"
                    >
                      Score
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {selectedMatch && (
        <MatchDetailsModal
          match={selectedMatch}
          onClose={() => setSelectedMatch(null)}
          navigate={navigate}
          getPlayerName={getPlayerName}
        />
      )}
    </div>
  );
}

function MatchDetailsModal({ match, onClose, navigate, getPlayerName }) {
  const [scoringHistory, setScoringHistory] = useState([]);

  useEffect(() => {
    // Build scoring history from service data
    const history = [];
    if (match.scores?.game1Service?.services) {
      match.scores.game1Service.services.forEach((service, idx) => {
        history.push({
          game: 1,
          serviceNumber: idx + 1,
          server: service.server === 'player1' ? (match.matchType === 'doubles' ? match.team1Name : match.player1Name) : (match.matchType === 'doubles' ? match.team2Name : match.player2Name),
          points: service.points,
          timestamp: service.timestamp
        });
      });
    }
    if (match.scores?.game2Service?.services) {
      match.scores.game2Service.services.forEach((service, idx) => {
        history.push({
          game: 2,
          serviceNumber: idx + 1,
          server: service.server === 'player1' ? (match.matchType === 'doubles' ? match.team1Name : match.player1Name) : (match.matchType === 'doubles' ? match.team2Name : match.player2Name),
          points: service.points,
          timestamp: service.timestamp
        });
      });
    }
    if (match.scores?.game3Service?.services) {
      match.scores.game3Service.services.forEach((service, idx) => {
        history.push({
          game: 3,
          serviceNumber: idx + 1,
          server: service.server === 'player1' ? (match.matchType === 'doubles' ? match.team1Name : match.player1Name) : (match.matchType === 'doubles' ? match.team2Name : match.player2Name),
          points: service.points,
          timestamp: service.timestamp
        });
      });
    }
    setScoringHistory(history);
  }, [match]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="match-details-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Match Details</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-content">
          <div className="match-summary">
            <h3>Match Summary</h3>
            <div className="summary-row">
              <span>Round:</span>
              <span>{match.round || 'N/A'}</span>
            </div>
            <div className="summary-row">
              <span>Type:</span>
              <span>{match.matchType === 'doubles' ? 'Doubles' : 'Singles'}</span>
            </div>
            <div className="summary-row">
              <span>Status:</span>
              <span className={`status-badge status-${match.status}`}>{match.status || 'scheduled'}</span>
            </div>
            {match.refereeName && (
              <div className="summary-row">
                <span>Referee:</span>
                <span>{match.refereeName}</span>
              </div>
            )}
            {match.startedAt && (
              <div className="summary-row">
                <span>Started:</span>
                <span>{formatDateTime(match.startedAt)}</span>
              </div>
            )}
            {match.completedAt && (
              <div className="summary-row">
                <span>Completed:</span>
                <span>{formatDateTime(match.completedAt)}</span>
              </div>
            )}
          </div>

          <div className="score-breakdown">
            <h3>Score Breakdown</h3>
            {match.scores && (
              <div className="games-scores">
                {match.scores.game1 && (
                  <div className="game-score-item">
                    <strong>Game 1:</strong> {match.scores.game1.player1} - {match.scores.game1.player2}
                  </div>
                )}
                {match.scores.game2 && (
                  <div className="game-score-item">
                    <strong>Game 2:</strong> {match.scores.game2.player1} - {match.scores.game2.player2}
                  </div>
                )}
                {match.scores.game3 && (
                  <div className="game-score-item">
                    <strong>Game 3:</strong> {match.scores.game3.player1} - {match.scores.game3.player2}
                  </div>
                )}
              </div>
            )}
          </div>

          {scoringHistory.length > 0 && (
            <div className="scoring-history">
              <h3>Service Sequence History</h3>
              <div className="history-list">
                {scoringHistory.map((item, index) => {
                  // Get service data
                  const serviceData = item.game === 1 
                    ? match.scores?.game1Service?.services?.[item.serviceNumber - 1]
                    : item.game === 2
                    ? match.scores?.game2Service?.services?.[item.serviceNumber - 1]
                    : match.scores?.game3Service?.services?.[item.serviceNumber - 1];
                  
                  if (!serviceData) return null;
                  
                  // Determine server name - use serverPlayerId if available (new format), otherwise fallback to teamServer (old format)
                  let serverName = item.server;
                  if (match.matchType === 'doubles') {
                    if (serviceData.serverPlayerId) {
                      // New format: use player ID
                      serverName = getPlayerName(serviceData.serverPlayerId);
                    } else if (serviceData.teamServer) {
                      // Old format: use team position (for backward compatibility)
                      if (serviceData.teamServer === 'team1Player1' && match.team1PlayerNames?.[0]) {
                        serverName = match.team1PlayerNames[0];
                      } else if (serviceData.teamServer === 'team1Player2' && match.team1PlayerNames?.[1]) {
                        serverName = match.team1PlayerNames[1];
                      } else if (serviceData.teamServer === 'team2Player1' && match.team2PlayerNames?.[0]) {
                        serverName = match.team2PlayerNames[0];
                      } else if (serviceData.teamServer === 'team2Player2' && match.team2PlayerNames?.[1]) {
                        serverName = match.team2PlayerNames[1];
                      } else {
                        serverName = serviceData.teamServer;
                      }
                    } else {
                      serverName = serviceData.server === 'player1' ? match.player1Name : match.player2Name;
                    }
                  } else if (match.matchType === 'singles') {
                    serverName = serviceData.server === 'player1' ? match.player1Name : match.player2Name;
                  }
                  
                  // Determine receiver name for doubles - use receiverPlayerId if available (new format), otherwise fallback to teamReceiver (old format)
                  let receiverName = null;
                  if (match.matchType === 'doubles') {
                    if (serviceData.receiverPlayerId) {
                      // New format: use player ID
                      receiverName = getPlayerName(serviceData.receiverPlayerId);
                    } else if (serviceData.teamReceiver) {
                      // Old format: use team position (for backward compatibility)
                      if (serviceData.teamReceiver === 'team1Player1' && match.team1PlayerNames?.[0]) {
                        receiverName = match.team1PlayerNames[0];
                      } else if (serviceData.teamReceiver === 'team1Player2' && match.team1PlayerNames?.[1]) {
                        receiverName = match.team1PlayerNames[1];
                      } else if (serviceData.teamReceiver === 'team2Player1' && match.team2PlayerNames?.[0]) {
                        receiverName = match.team2PlayerNames[0];
                      } else if (serviceData.teamReceiver === 'team2Player2' && match.team2PlayerNames?.[1]) {
                        receiverName = match.team2PlayerNames[1];
                      }
                    }
                  }
                  
                  // Determine winner and loser - use winnerPlayerId/loserPlayerId if available (new format), otherwise fallback to winner/loser (old format)
                  let winnerName, loserName;
                  if (match.matchType === 'doubles' && serviceData.winnerPlayerId) {
                    // New format: use player IDs
                    winnerName = getPlayerName(serviceData.winnerPlayerId);
                    loserName = serviceData.loserPlayerId ? getPlayerName(serviceData.loserPlayerId) : 'Unknown';
                  } else {
                    // Old format: use team/player names
                    const winner = serviceData.winner || serviceData.player;
                    const loser = serviceData.loser || (winner === 'player1' ? 'player2' : 'player1');
                    
                    winnerName = winner === 'player1'
                      ? (match.matchType === 'doubles' ? (match.team1Name || 'Team 1') : match.player1Name)
                      : (match.matchType === 'doubles' ? (match.team2Name || 'Team 2') : match.player2Name);
                    
                    loserName = loser === 'player1'
                      ? (match.matchType === 'doubles' ? (match.team1Name || 'Team 1') : match.player1Name)
                      : (match.matchType === 'doubles' ? (match.team2Name || 'Team 2') : match.player2Name);
                  }
                  
                  const servesCompleted = serviceData.servesCompleted || serviceData.points || 1;
                  const serverPoints = serviceData.serverPointsAtService !== undefined 
                    ? serviceData.serverPointsAtService 
                    : 0;
                  
                  return (
                    <div key={index} className="history-item">
                      <span className="history-game">Game {item.game}</span>
                      <span className="history-service">Service #{item.serviceNumber}</span>
                      <div className="history-details">
                        <div className="history-detail-row">
                          <span className="history-label">Player serving:</span>
                          <span className="history-value history-server-name">{serverName}</span>
                        </div>
                        {match.matchType === 'doubles' && receiverName && (
                          <div className="history-detail-row">
                            <span className="history-label">Served to:</span>
                            <span className="history-value history-receiver-name">{receiverName}</span>
                          </div>
                        )}
                        <div className="history-detail-row">
                          <span className="history-label">Player winning service:</span>
                          <span className="history-value history-winner">{winnerName}</span>
                        </div>
                        <div className="history-detail-row">
                          <span className="history-label">Serves completed:</span>
                          <span className="history-value history-serves-badge">{servesCompleted}</span>
                        </div>
                        <div className="history-detail-row">
                          <span className="history-label">Player losing service:</span>
                          <span className="history-value history-loser">{loserName}</span>
                        </div>
                        <div className="history-detail-row">
                          <span className="history-label">Server's points at service:</span>
                          <span className="history-value history-points-historic">{serverPoints}</span>
                        </div>
                        {item.timestamp && (
                          <div className="history-detail-row">
                            <span className="history-label">Time:</span>
                            <span className="history-value history-time">{formatTime(item.timestamp)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button onClick={() => navigate(`/match/${match.id}`)} className="btn-primary">
              View/Edit Match
            </button>
            <button onClick={onClose} className="btn-secondary">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

